// Share Project… (W9a, PROTOCOL.md §10): the owner invites a co-writer or a commenter with a link (copied, or shown as a
// QR code), sees who has accepted and compares the safety code with each of them — read aloud, or side by side — before
// confirming, which wraps the project key to them. Members are listed with their role (changeable), whether their code
// was compared ("Verified"), and Remove, which changes the project's key: wraps for the owner and everyone who stays,
// then every document uploaded again under the new key, with its progress shown. The limits (3 co-writers, 5
// commenters, invitations counted) are shown, and a refusal at a limit says why.
import { useCallback, useEffect, useState } from 'react';
import { errorText } from '@writing-suite/client/browser/usage.ts';
import { groupInviteCode } from '@writing-suite/client/net/invites.ts';
import * as S from '@writing-suite/client/net/shares.ts';
import { announce, useApp } from '../shell/context.ts';
import { isOnline } from '../store/account.ts';
import { projectTitle, type LibraryProject } from '../store/library.ts';
import { LiveProject } from '../store/project.ts';
import { authorName } from '../store/useProject.ts';
import { confirm, limitMessage, loadSharing, markVerified, ROLE_NAMES, rotateKey, type MemberRow, type RotationProgress, type ShareRole, type Sharing } from '../store/sharing.ts';
import { DialogShell } from './plan/BeatBoard.tsx';
import { ConfirmDialog } from './Menu.tsx';
import { QrCode } from './ReadersPage.tsx';

const DAY = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

export function rotationText(p: RotationProgress | null): string {
  if (!p) return '';
  if (p.step === 'key') return 'Making a new key…';
  if (p.step === 'wraps') return 'Giving the new key to everyone who stays…';
  if (p.step === 'upload') return `Uploading the project under the new key… ${p.done} of ${p.total} documents`;
  return 'The project’s key has changed.';
}

/** Runs `fn` with the project open live: the one given, else one opened for the purpose (and closed after). */
async function withLive<T>(app: ReturnType<typeof useApp>, project: LibraryProject, live: LiveProject | null, fn: (live: LiveProject) => Promise<T>): Promise<T> {
  if (live) return fn(live);
  const temp = new LiveProject(project.id, project.key, {
    api: app.account.session.api, store: app.account.store, online: isOnline, sessionEnded: app.sessionEnded, device: app.account.deviceId,
    memberId: app.account.accountId, author: authorName() ?? undefined,
  });
  try {
    await temp.open();
    return await fn(temp);
  } finally {
    await temp.flush().catch(() => {});
    temp.close();
  }
}

export function ShareDialog({ project, live, onClose }: { project: LibraryProject; live: LiveProject | null; onClose: () => void }) {
  const app = useApp();
  const title = projectTitle(project);
  const [sharing, setSharing] = useState<Sharing | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<ShareRole>('cowriter');
  const [made, setMade] = useState<(S.NewShare & { role: ShareRole }) | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MemberRow | null>(null);
  const [rotation, setRotation] = useState<RotationProgress | null>(null);
  const [comparing, setComparing] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setSharing(await loadSharing(app.account, project.id));
    } catch (e) {
      setError(errorText(e));
    }
  }, [app.account, project.id]);
  useEffect(() => {
    void refresh();
    // Someone accepting shows up without reopening the dialog.
    const t = setInterval(() => void refresh(), 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const act = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(limitMessage(e) ?? errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const makeLink = () =>
    act('link', async () => {
      const r = await S.createShare(app.account.session.api, project.id, { role });
      setMade({ ...r, role });
      // Shared from now on (W9c): the owner is recorded in the project's members, and every write after is attributed.
      await withLive(app, project, live, async (l) => l.recordMember()).catch(() => {});
      setShowQr(false);
      setCopied(false);
      announce('Link made');
    });

  const remove = (m: MemberRow) =>
    act('remove', async () => {
      await S.removeMember(app.account.session.api, project.id, m.account);
      announce(`Removed ${m.name ?? 'them'}`);
      // An active member had the key: it changes now, so they get nothing written from here on.
      if (m.status === 'active') {
        setRotation({ step: 'key', done: 0, total: 0 });
        await withLive(app, project, live, (l) => rotateKey({ account: app.account, projectId: project.id, key: l.key, live: l, summary: project.summary, onProgress: setRotation }));
        announce('The project’s key has changed');
        await app.refreshProjects();
      }
    });

  const limits = sharing?.limits ?? { cowriters: 3, commenters: 5 };
  const limitOf = (r: ShareRole) => (r === 'cowriter' ? limits.cowriters : limits.commenters);
  const full = (r: ShareRole) => !!sharing && sharing.used[r] >= limitOf(r);
  const now = Date.now();
  const pending = sharing?.members.filter((m) => m.status === 'pending') ?? [];
  const members = sharing?.members.filter((m) => m.status === 'active') ?? [];
  const invites = sharing?.invites.filter((i) => !i.acceptedAt && !i.revokedAt && i.expiresAt > now) ?? [];

  return (
    <DialogShell title={`Share “${title}”`} onCancel={onClose} className="share-dialog">
      <div data-share-dialog={project.id}>
        <p className="share-lede">Co-writers write in it with you; commenters comment and suggest changes. Only you compile it, make reader links and manage who’s in it.</p>
        <p className="share-limits" data-share-limits>
          {(['cowriter', 'commenter'] as const).map((r) => (
            <span key={r} data-limit-role={r} data-used={sharing?.used[r] ?? 0} data-limit={limitOf(r)}>
              {r === 'cowriter' ? 'Co-writers' : 'Commenters'} {sharing?.used[r] ?? 0} of {limitOf(r)}
            </span>
          ))}
        </p>

        <section className="share-section" aria-labelledby="share-invite">
          <h3 id="share-invite" className="eyebrow">Invite someone</h3>
          <fieldset className="share-roles">
            <legend className="visually-hidden">Role</legend>
            {(['cowriter', 'commenter'] as const).map((r) => (
              <label key={r} className={`share-role${role === r ? ' chosen' : ''}`}>
                <input type="radio" name="share-role" value={r} checked={role === r} onChange={() => setRole(r)} />
                <span>{ROLE_NAMES[r]}</span>
                {full(r) ? <small> (full)</small> : null}
              </label>
            ))}
          </fieldset>
          <p className="actions">
            <button type="button" className="button primary" onClick={() => void makeLink()} disabled={!!busy || !app.online}>
              {busy === 'link' ? 'Making…' : 'Make a Link'}
            </button>
          </p>
          {full(role) ? <p className="hint">{`A project can have up to ${limitOf(role)} ${role === 'cowriter' ? 'co-writers' : 'commenters'} besides you, counting invitations not yet accepted.`}</p> : null}
          {made ? (
            <div className="share-made" data-share-made data-role={made.role}>
              <p>
                A link for a {ROLE_NAMES[made.role].toLowerCase()}. It works once, until {DAY.format(new Date(made.expiresAt))}; it’s shown only now.
              </p>
              <p className="share-link">
                <code data-share-link>{made.link}</code>
              </p>
              <p className="share-code">Code <span data-share-code>{groupInviteCode(made.code)}</span></p>
              <p className="actions">
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => {
                    void navigator.clipboard?.writeText(made.link).then(() => setCopied(true)).catch(() => {});
                  }}
                >
                  {copied ? 'Copied' : 'Copy Link'}
                </button>
                <button type="button" className="button secondary" aria-expanded={showQr} onClick={() => setShowQr(!showQr)}>
                  QR code
                </button>
              </p>
              {showQr ? <QrCode text={made.link} label="QR code for the share link" /> : null}
            </div>
          ) : null}
        </section>

        {pending.length ? (
          <section className="share-section" aria-labelledby="share-pending">
            <h3 id="share-pending" className="eyebrow">Waiting for you</h3>
            <ul className="share-members">
              {pending.map((m) => (
                <li key={m.account} className="share-member pending" data-member={m.account} data-member-name={m.name ?? ''} data-status="pending">
                  <p className="share-member-name">
                    {m.name ?? 'Someone'} <span className="role-badge">{ROLE_NAMES[m.role]}</span>
                  </p>
                  <p className="safety">
                    Safety code <strong className="safety-code" data-safety-code>{m.safetyCode ?? '—'}</strong>
                  </p>
                  <p className="hint">Read it to each other: their screen shows a code too. Confirm only if the two are the same — a different code means someone else’s key.</p>
                  <p className="actions">
                    <button
                      type="button"
                      className="button primary small"
                      disabled={!!busy || !m.safetyCode}
                      onClick={() =>
                        void act('confirm', async () => {
                          await confirm(app.account, project.id, project.key, m);
                          await withLive(app, project, live, async (l) => l.recordMember()).catch(() => {});
                          announce(`${m.name ?? 'They'} can open “${title}” now`);
                        })
                      }
                    >
                      The codes match — Confirm
                    </button>
                    <button type="button" className="button secondary small" disabled={!!busy} onClick={() => void remove(m)}>
                      Don’t add
                    </button>
                  </p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="share-section" aria-labelledby="share-members">
          <h3 id="share-members" className="eyebrow">Members</h3>
          {members.length === 0 ? (
            <p className="empty">Only you, so far.</p>
          ) : (
            <ul className="share-members">
              {members.map((m) => (
                <li key={m.account} className="share-member" data-member={m.account} data-member-name={m.name ?? ''} data-status="active" data-verified={m.verified ? 'true' : 'false'}>
                  <p className="share-member-name">{m.name ?? 'Someone'}</p>
                  <label className="share-member-role">
                    <span className="visually-hidden">Role for {m.name ?? 'this member'}</span>
                    <select
                      value={m.role}
                      disabled={!!busy}
                      onChange={(e) => {
                        const next = e.target.value as ShareRole;
                        void act('role', async () => {
                          await S.setMemberRole(app.account.session.api, project.id, m.account, next);
                          announce(`${m.name ?? 'They'} ${next === 'cowriter' ? 'can write in it now' : 'can comment and suggest now'}`);
                        });
                      }}
                    >
                      <option value="cowriter">Co-writer</option>
                      <option value="commenter">Commenter</option>
                    </select>
                  </label>
                  {m.verified ? (
                    <span className="verified" data-verified-badge>Verified</span>
                  ) : (
                    <button type="button" className="link-button" aria-expanded={comparing === m.account} onClick={() => setComparing(comparing === m.account ? null : m.account)}>
                      Not verified
                    </button>
                  )}
                  <button type="button" className="link-button danger" disabled={!!busy} onClick={() => setRemoving(m)}>
                    Remove
                  </button>
                  {comparing === m.account && m.safetyCode ? (
                    <div className="safety compare">
                      <p>
                        Safety code <strong className="safety-code" data-safety-code>{m.safetyCode}</strong>
                      </p>
                      <p className="actions">
                        <button
                          type="button"
                          className="button secondary small"
                          onClick={() => {
                            markVerified(app.account.accountId, m.account, m.safetyCode!);
                            setComparing(null);
                            void refresh();
                          }}
                        >
                          The codes match
                        </button>
                      </p>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {invites.length ? (
          <section className="share-section" aria-labelledby="share-invites">
            <h3 id="share-invites" className="eyebrow">Links not yet used</h3>
            <ul className="share-members">
              {invites.map((i) => (
                <li key={i.shareId} className="share-member invite" data-share-invite={i.shareId}>
                  <p className="share-member-name">
                    {ROLE_NAMES[i.role]} <small>until {DAY.format(new Date(i.expiresAt))}</small>
                  </p>
                  <button
                    type="button"
                    className="link-button"
                    disabled={!!busy}
                    onClick={() => void act('revoke', async () => S.revokeShare(app.account.session.api, project.id, i.shareId))}
                  >
                    Withdraw
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {rotation ? (
          <p className="status-note rotation-note" role="status" data-rotation={rotation.step}>
            {rotationText(rotation)}
          </p>
        ) : null}
        {error ? (
          <p className="error" role="alert" data-share-error>
            {error}
          </p>
        ) : null}
        <div className="actions">
          <button type="button" className="button secondary" onClick={onClose} disabled={busy === 'remove'}>
            Done
          </button>
        </div>
      </div>
      {removing ? (
        <ConfirmDialog
          title={`Remove ${removing.name ?? 'them'}?`}
          message={
            <p>
              {removing.status === 'active'
                ? 'They lose the project at once, and its key changes, so nothing written from now on reaches them. What they already have stays with them.'
                : 'They won’t be added.'}
            </p>
          }
          confirm="Remove"
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            const m = removing;
            setRemoving(null);
            void remove(m);
          }}
        />
      ) : null}
    </DialogShell>
  );
}
